/**
 * ProtectedSectionComponent - Visual rendering for protected markdown sections
 *
 * Displays a read-only, visually distinct block with:
 * - Lock icon indicating protection
 * - Grayed-out styling
 * - Tooltip explaining why it's protected
 * - Non-interactive (cursor-not-allowed)
 */

import { Lock, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PROTECTED_SECTIONS } from './ProtectedSectionNode';
import { useStorageContext } from '@/contexts/use-storage';
import { useBevindingen } from '@/features/gebreken/hooks/useBevindingen';
import { useProjectImages } from '@/features/gebreken/hooks/useProjectImages';
import { GebrekenCardsDisplay } from '../../components/GebrekenCardsDisplay';

interface ProtectedSectionComponentProps {
  nodeKey: string;
  heading: string;
  placeholder: string;
}

const PROTECTED_SECTION_STYLES = {
  container:
    'bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-4 my-4 opacity-70 cursor-not-allowed select-none',
  header: 'flex items-center gap-2 mb-2 text-gray-600 dark:text-gray-400',
  icon: 'w-4 h-4 flex-shrink-0',
  title: 'font-semibold text-sm',
  content: 'font-mono text-sm text-gray-500 dark:text-gray-500 italic pl-6',
  infoContainer: 'mt-3 flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400 pl-6',
  infoIcon: 'w-3 h-3 flex-shrink-0 mt-0.5',
  infoText: 'leading-relaxed',
} as const;

export function ProtectedSectionComponent({
  nodeKey,
  heading,
  placeholder,
}: ProtectedSectionComponentProps) {
  const { t } = useTranslation('common');
  const { userId, conversationId } = useStorageContext();

  // Find the reason for protection from config
  const config = PROTECTED_SECTIONS.find((s) => s.heading === heading);

  // Get translated title and reason
  const title = t('editor.protectedSection.gebreken.title');
  const reason = config
    ? t('editor.protectedSection.gebreken.reason')
    : "Deze sectie is beschermd en kan alleen worden gewijzigd via gespecialiseerde tools.";

  // Fetch bevindingen data (real-time subscription)
  const { bevindingen, loading } = useBevindingen(
    userId || null,
    conversationId || null,
    { includeDeleted: false }
  );

  // Fetch images (one-time fetch)
  const { images, loading: imagesLoading } = useProjectImages(
    userId || '',
    conversationId || ''
  );

  // Filter to active only (extra safety, useBevindingen already handles this)
  const activeBevindingen = bevindingen.filter((b) => !b.deleted);

  // Decision logic:
  // 1. No userId/projectId → show locked placeholder
  // 2. Loading → show skeleton
  // 3. No bevindingen → show locked placeholder
  // 4. Has bevindingen → show cards

  if (!userId || !conversationId) {
    return <LockedPlaceholder nodeKey={nodeKey} title={title} placeholder={placeholder} reason={reason} />;
  }

  if (loading || imagesLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <GebrekenCardsDisplay
      bevindingen={activeBevindingen}
      images={images}
    />
  );
}

// Helper component: Locked Placeholder (current UI)
function LockedPlaceholder({ nodeKey, title, placeholder, reason }: {
  nodeKey: string;
  title: string;
  placeholder: string;
  reason: string;
}) {
  return (
    <div
      className={PROTECTED_SECTION_STYLES.container}
      data-lexical-protected-section="true"
      data-node-key={nodeKey}
      contentEditable={false}
      suppressContentEditableWarning
    >
      {/* Header with lock icon */}
      <div className={PROTECTED_SECTION_STYLES.header}>
        <Lock className={PROTECTED_SECTION_STYLES.icon} aria-hidden="true" />
        <span className={PROTECTED_SECTION_STYLES.title}>
          {title}
        </span>
      </div>

      {/* Placeholder content */}
      <div className={PROTECTED_SECTION_STYLES.content}>{placeholder}</div>

      {/* Information tooltip */}
      <div className={PROTECTED_SECTION_STYLES.infoContainer}>
        <Info className={PROTECTED_SECTION_STYLES.infoIcon} aria-hidden="true" />
        <span className={PROTECTED_SECTION_STYLES.infoText}>{reason}</span>
      </div>
    </div>
  );
}

// Helper component: Loading Skeleton
function LoadingSkeleton() {
  return (
    <div className="p-4 space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="border border-border p-4 space-y-2">
          <div className="h-4 bg-muted animate-pulse w-1/4"></div>
          <div className="h-3 bg-muted animate-pulse w-full"></div>
          <div className="h-3 bg-muted animate-pulse w-3/4"></div>
        </div>
      ))}
    </div>
  );
}
